/**
 * Unified product record — normalized from 4 different Benefit API response shapes.
 *
 * API field mapping (see docs/PRODUCT_SEARCH_API_SHAPES.md):
 *   FA:   productid, name(PADDED), companyNa, bestid, surryr, intrateter, rop(0/1), mva(0/1), statesAvail
 *   IA:   productid, name(PADDED), companyNa(padded), bestid, statesAvail, mva, death, gmwb/gmib/gmab("Y"/null), closeddate, incdate, feebased
 *   VA:   id/productId, productName, name(=COMPANY!), cid, bestid, shareClass, meCharge1, adminCharge, gmwb/gmib/gmab("Y"/null), gmwbCnt/gmibCnt/gmabCnt/dbCnt/bonusCnt, statesAvail
 *   RILA: same as VA — from GetAllIVAProducts (productPolicyTypeId=4)
 *
 * `statesAvail` is the state-availability key on all four, and the column is
 * now present on every list view (VProductProfileIndexProductList,
 * VProductProfileFixedProductApi, VProductProfileVaIvaProductList) — it shipped
 * on IA first, so older notes here describing FA and VA/IVA as pending are out
 * of date. Individual rows may still have it null, so state filters no-op per
 * product rather than excluding rows that lack it.
 */
export interface SearchProduct {
  // Identity
  id: number;
  originalProductId: number;
  productType: 'FA' | 'IA' | 'VA' | 'RILA';

  // Display
  companyName: string;
  productName: string;
  amBest: string;

  // CUSIP
  cusipNo: string | null;
  cusipName: string | null;
  cusipId: number | null;
  cusipDesc: string | null;
  cusipInceptionDate: string | null;

  // Type-specific (show "—" when not applicable)
  shareClass: string | null;       // VA/RILA only
  feeBased: boolean | null;        // IA only
  meCharge: number | null;         // VA/RILA only (decimal, display as %)
  adminCharge: number | null;      // VA/RILA only (decimal, display as %)
  // Populated for all four types, from a differently-named column each: FA
  // `surryr`, IA `SurrYr`, VA/RILA `CdscTerm`. Normalized to one CSV shape
  // ("5, 7") because the option list and filter predicate match on exact text.
  surrenderYears: string | null;   // CSV: "5, 7"
  rateTerms: string | null;        // FA only (CSV: "5, 7")
  prodType: string | null;         // FA prodtyp mapped label
  premType: string | null;         // Premium type label; source + code map differ per product type

  // Contribution limits — MinimumContribution / MaximumContribution off each
  // product type's GetAllProducts response. null = the row didn't supply one,
  // in which case the premium filters no-op for that product rather than
  // excluding it (same convention as statesAvail above).
  minPremium: number | null;       // lowest premium the product accepts
  maxPremium: number | null;       // highest premium the product accepts

  // Booleans
  rop: boolean;
  mva: boolean;
  gmwb: boolean;
  gmib: boolean;
  gmab: boolean;
  bonus: boolean;
  deathBenefit: boolean;

  // Waivers (IA only has hosp; FA has none from GetAllProducts; VA/RILA have none)
  // These will be populated from L2 detail calls in Phase 2 — stub as null for now
  waiverDeath: boolean | null;
  waiverAnnuitization: boolean | null;
  waiverHospitalization: boolean | null;

  // Counts (VA/RILA only)
  gmwbCnt: number;
  gmibCnt: number;
  gmabCnt: number;
  dbCnt: number;
  bonusCnt: number;

  // Status
  closedDate: string | null;       // null = open
  /** VA/RILA only: va_products.is_terminated = 1. Closed even with no close
   *  date — see isRowClosed(). */
  isTerminated?: boolean;
  introDate: string | null;
  statesCount: number | null;      // from statesAvail; null = no state data

  // Carrier branding — populated via /api/Company/GetAll join on companyid
  // Value is a relative path like "\\CompanyLogo\\79_logo1.png"; CarrierLogoComponent
  // converts to S3 URL. Null when company has no uploaded logo.
  logoPath: string | null;

  // Raw data for L2 detail panel
  _raw: any;
}

/**
 * Is this row closed? A close date (any non-blank value) closes every type;
 * for VA/RILA, is_terminated = 1 closes it too, with or without a date. The
 * product-level Status filter, counts and Status column all build on this.
 */
export function isRowClosed(p: Pick<SearchProduct, 'closedDate' | 'isTerminated'>): boolean {
  return !!p.closedDate?.toString().trim() || p.isTerminated === true;
}

/** is_terminated arrives as 1/0, true/false or "1"/"0" depending on the view. */
export function terminatedFlag(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || (typeof v === 'string' && v.trim().toLowerCase() === 'true');
}

/**
 * Surrender-years sentinel. The feeds encode "no surrender period" as -3, which
 * would otherwise render as a bare "-3" and — being negative — sort ahead of
 * every real term. Only -3 is special-cased; any other value is treated as a
 * literal year count.
 */
export const SURR_YEARS_UNLIMITED = '-3';

/** Display token for a surrender-years value. Bare, with no "yr" suffix — the
 *  legacy dropdown appends its own unit, the rail picker doesn't. */
export function surrYearsLabel(v: string): string {
  return v === SURR_YEARS_UNLIMITED ? 'Unlimited' : v;
}

/** Option-label overrides for the Surr Years pickers, keyed by raw value. */
export const SURR_YEARS_LABELS: Record<string, string> = {
  [SURR_YEARS_UNLIMITED]: 'Unlimited',
};

/** Ascending by term, with the Unlimited sentinel pinned last. */
export function compareSurrYears(a: string, b: string): number {
  const au = a === SURR_YEARS_UNLIMITED;
  const bu = b === SURR_YEARS_UNLIMITED;
  if (au !== bu) return au ? 1 : -1;
  return Number(a) - Number(b);
}

/** Product type metadata for pills and badges */
export const PRODUCT_TYPE_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  FA:   { label: 'FA',   color: '#2E7D32', bg: '#E8F5E9', border: '#B8D8BA' },
  IA:   { label: 'IA',   color: '#6B4C9A', bg: '#F0EDF7', border: '#D4CBE8' },
  VA:   { label: 'VA',   color: '#1A5FA6', bg: '#EDF4FA', border: '#C4D9EE' },
  RILA: { label: 'RILA', color: '#E65100', bg: '#FFF3E0', border: '#FFCC80' },
};
